/**
 * SignUpWrapper - Feature flag wrapper for sign-up page
 *
 * When PaymentPageV2 flag is enabled, redirects to payment page (skips sign-up)
 * EXCEPT for VIP invite codes - those need the sign-up flow to validate VIP access
 * and potentially bypass payment entirely.
 *
 * Otherwise renders the legacy sign-up page
 */

import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { FeatureFlags, useFeatureFlags } from '@/services/feature-flag-provider';
import { ROUTE_PAYMENT_COLLECT_PAYMENT_METHOD } from '@/constants/routes';
import { getRoute } from '@/utils';
import authService from '@/services/auth.service';
import Page from './sign-up.page';

/**
 * Check if an invite code is a VIP code that requires the sign-up flow
 * VIP codes start with "VIP_" or "VIP-" or end with "_VIP" or "-VIP" (case insensitive)
 */
function isVipInviteCode(code: string | null | undefined): boolean {
  if (!code) return false;
  const lowerCode = code.toLowerCase();
  return (
    lowerCode.startsWith('vip_') ||
    lowerCode.startsWith('vip-') ||
    lowerCode.endsWith('_vip') ||
    lowerCode.endsWith('-vip')
  );
}

export function SignUpWrapper() {
  const { isEnabled, isLoading } = useFeatureFlags();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Show blank screen while feature flags are loading to prevent flash
  if (isLoading) {
    return <div className="min-h-screen bg-white" />;
  }

  // Check for VIP invite code from URL or localStorage
  const urlInviteCode = searchParams.get('invite-code');
  const storedInviteCode = authService.getInviteCode();
  const hasVipCode = isVipInviteCode(urlInviteCode) || isVipInviteCode(storedInviteCode);

  const showV2 = isEnabled(FeatureFlags.IsPaymentPageV2Enabled);

  // VIP users must go through sign-up flow to validate VIP access
  // and potentially bypass payment entirely
  if (showV2 && !hasVipCode) {
    // Redirect to payment page, preserving query params
    return <Navigate to={`${getRoute(ROUTE_PAYMENT_COLLECT_PAYMENT_METHOD)}${location.search}`} replace />;
  }

  return <Page />;
}

export default SignUpWrapper;
